import type { Appearance, Theme, ThemePort } from '@thom/folio-domain/core/ports/theme'
import { createEmitter } from '@thom/folio-domain/lib/emitter'
import { THEME, THEMES } from '@thom/folio-domain/core/ports/theme'

const STORAGE_KEY = 'folio.theme'

const isTheme = (value: unknown): value is Theme => THEMES.includes(value as Theme)

const read = (): Theme => {
	try {
		const stored = localStorage.getItem(STORAGE_KEY)
		return isTheme(stored) ? stored : THEME.SYSTEM
	} catch {
		return THEME.SYSTEM
	}
}

const systemQuery = () => window.matchMedia('(prefers-color-scheme: dark)')

const resolve = (theme: Theme): Appearance => {
	if (theme !== THEME.SYSTEM) return theme
	return systemQuery().matches ? THEME.DARK : THEME.LIGHT
}

const applyAppearance = (appearance: Appearance): void => {
	document.documentElement.classList.toggle('dark', appearance === THEME.DARK)
	document.documentElement.style.colorScheme = appearance
}

const createThemeAdapter = (): ThemePort => {
	const changes = createEmitter()
	let theme = read()

	const sync = () => {
		applyAppearance(resolve(theme))
		changes.emit()
	}

	return {
		current: () => theme,

		resolved: () => resolve(theme),

		set: next => {
			theme = next
			try {
				localStorage.setItem(STORAGE_KEY, next)
			} catch {
				// A blocked store still themes this page; it just will not persist.
			}
			sync()
		},

		subscribe: listener => {
			const stop = changes.on(listener)
			const media = systemQuery()
			const onSystemChange = () => {
				if (theme === THEME.SYSTEM) sync()
			}

			media.addEventListener('change', onSystemChange)

			return () => {
				stop()
				media.removeEventListener('change', onSystemChange)
			}
		},
	}
}

export { applyAppearance, createThemeAdapter }
